/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, Divider, styled, TextField } from '@ethings/ui-toolkit';
import { AutocompleteValue, IconButtonProps, Paper, PaperProps, PopperProps } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { isEmpty, isString } from 'lodash';
import { HTMLAttributes, PropsWithChildren, useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';
import { ulid } from 'ulid';
import { API_CALL_TEXT_LENGTH } from '../../constants';
import { useObservable } from '../../hooks';
import { testid } from '../../util/test-id';
import { ErrorMessage } from '../error-message';
import { Icon } from '../icons';
import { MiddleSpinner } from '../middle-spinner';
import { LookupProps } from './types';

interface OptionState {
  selected: boolean;
}

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const SELECT_ALL = 'SELECT_ALL';

/**
 * FUNCTION: Note this is required to be a function in React to achieve strong generic typing :(
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function Lookup<T, Multiple extends boolean | undefined = undefined>({
  debounceTime: debounce = 0,
  disabled = false,
  disableClearable,
  disableFilter,
  displaySelectAll,
  error: externalError,
  helperText: externalHelperText,
  inputAdornment,
  fullWidth,
  getOptionTestId,
  handleCompareItems,
  handleFindItems,
  handleFormatListItem = (item) => <span>{String(item)}</span>,
  handleFormatSelectedItem = (item) => String(item),
  handleRenderTags,
  initialInstructions,
  isSearch = false,
  label,
  loadOnChange = true,
  loadOnRender,
  minSearchLength = API_CALL_TEXT_LENGTH,
  multiple,
  noResultsInstructions,
  onInputChange,
  onItemSelected = () => { },
  optionStyle,
  openOnFocus,
  placeholder,
  renderDropdownHeader,
  required,
  selectedItem,
  size,
  textFieldStyles,
  updatableData = false,
  disablePortal = true,
  ...props
}: PropsWithChildren<LookupProps<T, Multiple>> & { inputAdornment?: JSX.Element }): JSX.Element {
  const [inputLength, setInputLength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLookupOpen, setIsLookupOpen] = useState(false);
  const [error, setError] = useState<Error>();
  const clearError = (): void => setError(undefined);
  const renderError = (): void => setError(new Error());
  const { t } = useTranslation(['common']);

  const { handleQueryChanged, observable } = useMemo(() => {
    const subject = new Subject<string>();

    return {
      handleQueryChanged: (query = ''): void => subject.next(query),
      observable: subject.pipe(
        tap(() => {
          setIsLoading(true);
          clearError();
        }),
        debounceTime(debounce),
        switchMap((query: string) => {
          if (isSearch && query.length < minSearchLength) return of(undefined);
          const deferred = handleFindItems(query);

          return from(deferred).pipe(
            catchError((err: Error) => {
              setError(err);
              return of(undefined);
            }),
          );
        }),
        tap(() => setIsLoading(false)),
      ),
    };
  }, [handleFindItems]);
  const data = useObservable(observable);

  const renderOption = (optionProps: HTMLAttributes<HTMLLIElement>, option: T, optionState: OptionState): JSX.Element => {
    const key = ulid();
    const testId = getOptionTestId ? getOptionTestId(option) : testid`${key}`;

    if (multiple) {
      return <>
        <li {...optionProps} key={key} data-testid={testId} style={{ ...optionProps.style, ...optionStyle }}>
          <StyledCheckbox
            icon={<Icon icon="Square" fontSize="small" />}
            checkedIcon={<Icon icon="CheckSquare" fontSize="small" />}
            checked={option === SELECT_ALL ? data?.length === (selectedItem as any[]).length : optionState.selected}
          />
          {option === SELECT_ALL ? <strong>{t('common:component.filter-dropdown.labels.select-all')}</strong> : handleFormatListItem(option)}
        </li>
        {option === SELECT_ALL && <Divider />}
      </>;
    }
    return <li {...optionProps} key={key} data-testid={testId} style={{ ...optionProps.style, ...optionStyle }}>{handleFormatListItem(option)}</li>;
  };

  const labelWithChildren = label as { props?: { children?: string } };
  const dataTestId = props['data-testid'] || testid`lookup-${isString(label) ? label : labelWithChildren?.props?.children}`;

  useEffect(() => {
    if (!loadOnRender) return;
    handleQueryChanged();
  }, [loadOnRender, handleQueryChanged]);

  const DropdownPaper = renderDropdownHeader
    ? (paperProps: PaperProps): JSX.Element => (
      <Paper {...paperProps}>
        {renderDropdownHeader({ closeDropdown: () => setIsLookupOpen(false) })}
        {paperProps.children}
      </Paper>
    )
    : undefined;

  return (
    <Autocomplete<T, Multiple, boolean>
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingRight: '0px !important',
        },
      }}
      blurOnSelect="touch"
      clearOnEscape
      componentsProps={{
        popper: {
          'data-testid': `${dataTestId}-popper`,
        } as Partial<PopperProps>, // Incorrect type in @mui/material doesn't allow passing data attributes, so we need the assertion.
        popupIndicator: {
          'data-testid': `${dataTestId}-button`,
        } as Partial<IconButtonProps>,
      }}
      data-testid={dataTestId}
      disableClearable={disableClearable}
      disableCloseOnSelect={multiple}
      disabled={disabled}
      filterOptions={disableFilter ? undefined : (options) => {
        if (multiple && displaySelectAll) {
          return [SELECT_ALL as T, ...options];
        }
        return options;
      }}
      fullWidth={fullWidth}
      getOptionLabel={handleFormatSelectedItem}
      isOptionEqualToValue={handleCompareItems}
      loading={isLoading}
      loadingText={<MiddleSpinner />}
      multiple={multiple}
      noOptionsText={(isSearch && inputLength < minSearchLength)
        ? t('common:component.search.hint.less-than-count-map', { count: minSearchLength })
        : noResultsInstructions
      }
      options={isLoading && updatableData ? [] : data ?? []}
      onChange={(_event, newValue) => {
        if (multiple && (newValue as any[]).includes(SELECT_ALL)) {
          const isAllSelected = data?.length === (selectedItem as any[]).length;
          onItemSelected(((isAllSelected ? [] : data) ?? []) as AutocompleteValue<T, Multiple, false, false>);
        }
        else {
          onItemSelected(newValue);
        }
        if (required && (isEmpty(newValue) || !newValue)) return renderError();
        clearError();
      }}
      onError={() => renderError()}
      onInputChange={(event, newInputValue, reason) => {
        onInputChange?.(event, newInputValue, reason);
        setInputLength(newInputValue.length);
        if (!loadOnChange) return;
        handleQueryChanged(newInputValue);
      }}
      {...(renderDropdownHeader
        ? {
          onClose: () => setIsLookupOpen(false),
          onOpen: () => {
            setIsLookupOpen(true);
            if (!loadOnRender) handleQueryChanged();
          },
          open: isLookupOpen,
        }
        : {
          onOpen: () => {
            if (!loadOnRender) handleQueryChanged();
          },
        })}
      openOnFocus={openOnFocus}
      PaperComponent={DropdownPaper}
      renderInput={(params) => (
        <TextField
          {...params}
          data-testid="text-input"
          InputProps={{
            ...params.InputProps,
            endAdornment: inputAdornment ?? params.InputProps?.endAdornment ?? null,
          }}
          error={externalError || !!error}
          helperText={error ? (externalHelperText ?? <ErrorMessage error={error} />) : (externalError ? externalHelperText : initialInstructions)}
          label={label}
          required={required}
          placeholder={placeholder}
          style={textFieldStyles}
          variant="outlined"
        />
      )}
      renderOption={renderOption}
      renderTags={handleRenderTags}
      size={size}
      value={selectedItem}
      disablePortal={disablePortal}
    />
  );
}
